import React, { memo } from 'react';
import cn from 'classnames';
import Head from 'next/head';

import { MatchesSkeleton } from 'components/skeletons/dashboard.component';
import { useCandidate } from 'hooks/useCandidate';
import { useCandidateMatches } from 'lib/providers/CandidateMatchesProvider';
import { useCandidateDashboard } from 'lib/providers/CandidateDashboardProvider';
import MatchesBanner from '../MatchesBanner';

import styles from './Matches.module.scss';

export interface MatchesProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  onClickPastMatches?: () => void;
  style?: Record<string, unknown>;
  onOpenMatchingPreferencesModal: () => void;
  onOpenOnboarding: () => void;
}

export const Matches: React.FC<MatchesProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    onClickPastMatches,
    onOpenMatchingPreferencesModal,
    onOpenOnboarding,
  }: MatchesProps) => {
    const contentClassNames = cn(
      {
        [styles.Matches]: true,
      },
      className
    );
    const { isNonOnboardedUser } = useCandidate();
    const { candidateLastUpdatedStep, isCandidateLastStepLoading } =
      useCandidateDashboard();
    const {
      matchesMeta,
      opportunityMatches,
      isMatchesLoading,
      isMatchesGenerating,
      isMatchesRegenerating,
    } = useCandidateMatches();
    const { unvisited_count, total_count } = matchesMeta || {};

    if (
      isMatchesLoading ||
      isMatchesGenerating ||
      isMatchesRegenerating ||
      unvisited_count === undefined ||
      total_count === undefined ||
      isCandidateLastStepLoading ||
      candidateLastUpdatedStep === undefined
    ) {
      return (
        <div className={cn(styles.spinner, className)}>
          <MatchesSkeleton />
        </div>
      );
    }

    return (
      <>
        <Head>
          <title>
            {unvisited_count
              ? `(${unvisited_count}) Dashboard | Huzzle`
              : `Dashboard | Huzzle`}
          </title>
          <meta name="description" content="Huzzle" />
        </Head>
        <div className={contentClassNames} style={style} data-testid="Matches">
          <MatchesBanner
            onClickPastMatches={onClickPastMatches}
            isNonOnboardedUser={isNonOnboardedUser}
            matchList={opportunityMatches}
            onOpenMatchingPreferencesModal={onOpenMatchingPreferencesModal}
            onOpenOnboarding={onOpenOnboarding}
          />
        </div>
      </>
    );
  }
);

Matches.displayName = 'Matches';
